import { AnyObject } from '@xbcb/shared-types';
import { transformEuCustomsEntry } from '../euCustomsEntry';
import moment from 'moment';
import {
  CustomsDeclaration,
  GoodsShipmentInput,
  TransportContractDocument,
  TransportContractDocumentType,
} from '__generated__/graphql';
import { getAllGoodsShipmentSnapshots } from 'libs/formCore/transforms/goodsShipmentFormRef';
import { CuDeGoodsShipmentTransformer } from 'libs/formCore/transforms';

const goodsShipmentTransformer = new CuDeGoodsShipmentTransformer();

// These are queried for display by AppRecord but are NOT valid on
// UpdateCustomsDeclarationInput. They leak through because the Ant Form
// holds the full record as initialValues. None of these are managed by
// our RHF forms — they come from the legacy Ant Form path.
const ANT_FORM_FIELDS_TO_STRIP = [
  'auditLog',
  'canceled',
  'badges',
  'identification',
  'id',
  'version',
  'created',
  'updated',
  'deleted',
  'tags',
  '__typename',
];

// Map the nested goodsShipment shape returned by the CustomsDeclaration query
// into the flat namepaths the shared sub-forms (Manifest, Containers,
// ConveyanceForm, DepartureForm, ArrivalForm) expect.
const toForm = ({
  existingRecord,
}: {
  existingRecord: AnyObject;
}): AnyObject => {
  const record = { ...existingRecord } as AnyObject &
    Partial<CustomsDeclaration>;
  const gs = record.goodsShipment?.[0] ?? {};
  const consignment = gs.consignment?.[0] ?? {};
  const btm = consignment.borderTransportationMean?.[0] ?? {};
  const arrivalLoc = btm.arrivalLocation?.[0] ?? {};
  const departureLoc = btm.departureLocation?.[0] ?? {};
  const docs: TransportContractDocument[] =
    consignment.transportContractDocument ?? [];
  const equipment = consignment.transportEquipment ?? [];

  // Manifest: masterBills → [ { number, houseBills: [{ number }] } ]
  const masterDocs = docs.filter(
    (d) => d.type === TransportContractDocumentType.MASTER_BILL_OF_LADING,
  );
  const houseDocs = docs.filter(
    (d) =>
      d.type === TransportContractDocumentType.HOUSE_BILL_OF_LADING ||
      d.type === TransportContractDocumentType.HOUSE_WAYBILL,
  );
  const houseBills = houseDocs.map((h) => ({ number: h.identification }));
  record.masterBills = masterDocs.length
    ? masterDocs.map((d) => ({ number: d.identification, houseBills }))
    : [{ number: undefined, houseBills }];

  // Containers
  record.containers = equipment.map((eq) => ({
    number: eq.identification,
    description: eq.name,
    type: eq.fullness,
  }));

  // Conveyance
  record.conveyance = {
    conveyanceName: btm.name,
    tripNumber: btm.journey,
    modeOfTransport: btm.identificationType,
    containerized: consignment.containerized,
    vesselCountryFlag: undefined,
    grossWeight: undefined,
  };

  // Departure
  record.departure = {
    portOfLadingCode: departureLoc.identification,
    exportCountryCode: gs.exportCountry?.[0]?.country,
    exportDate: btm.departure ? moment(btm.departure) : undefined,
  };

  // Arrival
  record.arrival = {
    portOfUnladingCode: arrivalLoc.identification,
    importDate: btm.arrival ? moment(btm.arrival) : undefined,
    usDestinationStateCode: gs.importCountry?.[0]?.country,
  };

  return record;
};

export const transformCustomsDeclaration = {
  toForm,
  toSchema: ({
    input,
    existingRecord,
  }: {
    input: AnyObject;
    existingRecord: AnyObject;
  }): AnyObject & { goodsShipment?: GoodsShipmentInput[] } => {
    const cleanedOutput = transformEuCustomsEntry.toSchema({
      input,
      existingRecord,
    });

    // Strip Ant Form fields not valid on the mutation input
    for (const field of ANT_FORM_FIELDS_TO_STRIP) {
      delete cleanedOutput[field];
    }

    // goodsShipment is managed by our RHF forms (GoodsShipmentForm).
    // Merge edited snapshots with original data: visited shipments use the
    // snapshot, unvisited shipments pass through their original data.
    const shipmentSnapshots = getAllGoodsShipmentSnapshots();
    const originalShipments = (existingRecord as any)?.goodsShipment ?? [];

    if (shipmentSnapshots.size > 0 || originalShipments.length > 0) {
      cleanedOutput.goodsShipment = originalShipments.map(
        (orig: any, idx: number) => {
          const snap = shipmentSnapshots.get(idx);
          if (snap) {
            return goodsShipmentTransformer.toSchema(
              snap.formData,
              snap.original,
              snap.shipmentIndex,
            );
          }
          // Unvisited shipment: pass through original (strip party refs to id-only)
          return goodsShipmentTransformer.toSchema(
            goodsShipmentTransformer.toForm(orig),
            orig,
            idx,
          );
        },
      );
    } else {
      delete cleanedOutput.goodsShipment;
    }

    return cleanedOutput;
  },
};
